import React, { createContext, useEffect, useState } from "react";
import {
	getAllLabelsForCurrentTopic,
	getUserWithTopicAccesses,
} from "../../services/fetch";

import {
	Categories,
	LabelOutputType,
	RawCategories,
	Topic,
	UserWithTopics,
	UserRole,
	Filter,
	UserPreferences,
	FilterType,
	FilterItem,
} from "./types";
import { useClientAccessContext } from "../ClientAccessContext";
import { SortItem } from "../../components/GenericSortItem/types";
import { defaultSortOrder } from "../../components/GenericSortItem";
import { initialFilters } from "./initialFilters";
import { subWeeks } from "date-fns";
import { SearchQuery } from "../../services/types";

export interface IConfigProvider {
	selectedTopic: Topic;
	usersTopics: Topic[];
	categories: Categories;
	numericLabels: Categories;
	loadingTopics: boolean;
	loadingCategories: boolean;
	user: UserWithTopics;
	sortOrder: SortItem;
	filters: Filter[];
	startDate: Date;
	endDate: Date;
	fromNavBar: boolean;
	searchQuery: SearchQuery;
	queryParams: { [k: string]: string };
	setFromNavBar: React.Dispatch<React.SetStateAction<boolean>>;
	setSortOrder: React.Dispatch<React.SetStateAction<SortItem>>;
	setSelectedTopic: React.Dispatch<React.SetStateAction<Topic>>;
	setLoadingTopics: React.Dispatch<React.SetStateAction<boolean>>;
	toggleFilterChecked: (
		filterName: string,
		filterValue: string | null,
		uncheckAll?: boolean
	) => void;
	getCheckedItems: (filterType: FilterType) => string[] | number[] | boolean;
	setStartDate: React.Dispatch<React.SetStateAction<Date>>;
	setEndDate: React.Dispatch<React.SetStateAction<Date>>;
	setSearchQuery: React.Dispatch<React.SetStateAction<SearchQuery>>;
	setQueryParams: React.Dispatch<React.SetStateAction<{ [k: string]: string }>>;
}

interface ConfigProps {
	children?: React.ReactNode;
}

const ConfigContext = createContext({} as IConfigProvider);

const initSearchQuery: SearchQuery = {
	includeText: "",
	excludeText: "",
	includeDocuments: [],
	excludeDocuments: [],
};

const ConfigProvider: React.FunctionComponent<ConfigProps> = ({ children }) => {
	const { selectedClient } = useClientAccessContext();
	const [loadingCategories, setLoadingCategories] = useState(true);
	const [loadingTopics, setLoadingTopics] = useState(false);
	const [usersTopics, setUsersTopics] = useState<[] | Topic[]>([]);
	const [selectedTopic, setSelectedTopic] = useState<null | Topic>(null);
	const [user, setUser] = useState<UserWithTopics>(null);
	const [sortOrder, setSortOrder] = useState<SortItem>(defaultSortOrder);
	const [startDate, setStartDate] = useState(subWeeks(new Date(), 1));
	const [endDate, setEndDate] = useState(new Date());
	const [fromNavBar, setFromNavBar] = useState(false);
	const [searchQuery, setSearchQuery] = useState<SearchQuery>(initSearchQuery);
	const [queryParams, setQueryParams] = useState<{ [k: string]: string }>({});

	const [categories, setCategories] = useState<null | Categories>(null);
	const [filters, setFilters] = useState<Filter[]>(null);
	const [numericLabels, setNumericLabels] = useState<null | Categories>(null);

	useEffect(() => {
		if (!selectedClient) return;
		const fetchTopicAccesses = async () => {
			setLoadingTopics(true);
			setUser(null);
			setUsersTopics([]);
			const response = await getUserWithTopicAccesses();
			const user = response?.result || null;
			if (user && user.clientId !== selectedClient.clientId)
				window.location.reload();
			const topics: Topic[] = user?.topics || [];

			if (topics.length) {
				const localStorageKey = `${user.id}-${selectedClient.clientId}-selectedTopicId`;
				const storedSelectedTopicId = localStorage.getItem(localStorageKey);
				const findTopic = topics.find((t) => t.id === +storedSelectedTopicId);
				if (storedSelectedTopicId && findTopic) {
					setSelectedTopic(findTopic);
				} else {
					setSelectedTopic(topics.length === 1 ? topics[0] : null);
				}
			}

			setUser(user);
			setUsersTopics(topics);
			setLoadingTopics(false);
		};
		setSelectedTopic(null);
		fetchTopicAccesses();
	}, [selectedClient]);

	useEffect(() => {
		if (!selectedTopic) return setFilters(null);
		setLoadingCategories(true);
		const localStorageKey = `${user.id}-${selectedClient.clientId}-selectedTopicId`;
		localStorage.setItem(localStorageKey, selectedTopic.id.toString());

		const fetchCategories = async () => {
			const response = await getAllLabelsForCurrentTopic(selectedTopic.id);
			const categories = response.result.filter(
				(l) => l.type === LabelOutputType.CATEGORY
			);
			const numeric = response.result.filter(
				(l) => l.type === LabelOutputType.RELEVANCY
			);

			const mappedCategories = mapLabels(categories);
			const mappedNumeric = mapLabels(numeric);
			setCategories(mappedCategories);
			setNumericLabels(mappedNumeric);
			setFilters([...createFilters(mappedCategories), ...initialFilters]);

			setLoadingCategories(false);
		};
		fetchCategories();
	}, [selectedTopic]);

	const toggleFilterChecked = (
		filterName: string,
		itemValue: string,
		uncheckAll = false
	) => {
		setFilters((prevFilters) => {
			return prevFilters.map((filter) => {
				if (filter.filterName === filterName) {
					return {
						...filter,
						filterItems: updateFilterItems(filter, itemValue, uncheckAll),
					};
				}
				return filter;
			});
		});
	};

	const removeQueryParams = () => {
		setQueryParams({});
		const params = new URLSearchParams(window.location.search);
		params.delete("document_id");
		const newUrl = `${window.location.pathname}?${params.toString()}`;
		window.history.replaceState(null, "", newUrl);
	};

	const getCheckedItems = (filterType: FilterType) => {
		let checkedItems: any = [];

		if (!filters?.length) return null;

		filters.forEach((filter) => {
			if (filter.type === filterType) {
				const checked = filter.filterItems.filter((item) => item.checked);
				switch (filterType) {
					case FilterType.LANGUAGE:
						checkedItems = checked.map((item) => item.value);
						break;
					case FilterType.CATEGORY:
						checkedItems = [
							...checkedItems,
							...checked.map((item) => item.possibleLabelValueId),
						];
						break;
					case FilterType.CHECKED:
						const checkedItem = checked.find((item) =>
							["yes", "no"].includes(item.filterLabel)
						);
						if (checkedItem) {
							checkedItems = checkedItem.filterLabel === "yes";
						} else {
							checkedItems = null;
						}
						break;
					default:
						break;
				}
			}
		});

		return checkedItems;
	};

	const configContextValue = {
		usersTopics,
		selectedTopic,
		categories,
		loadingTopics,
		loadingCategories,
		numericLabels,
		user,
		sortOrder,
		filters,
		endDate,
		startDate,
		fromNavBar,
		searchQuery,
		queryParams,
		setFromNavBar,
		setSortOrder,
		setSelectedTopic,
		setLoadingTopics,
		toggleFilterChecked,
		getCheckedItems,
		setStartDate,
		setEndDate,
		setSearchQuery,
		setQueryParams,
	};
	return (
		<ConfigContext.Provider value={configContextValue}>
			{children}
		</ConfigContext.Provider>
	);
};

const useConfig = () => {
	const context = React.useContext(ConfigContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within a AuthProvider");
	}
	return context;
};

export { ConfigProvider, useConfig };

const mapLabels = (categories: RawCategories[]) => {
	const mappedCategories: Categories = {};
	categories.forEach((c) => {
		mappedCategories[c.name] = c.possibleLabelValues;
	});

	return mappedCategories;
};

const createFilters = (categories) => {
	let filters: Filter[] = [];

	Object.keys(categories).forEach((c) => {
		let filterItems: FilterItem[] = categories[c].map((l) => ({
			filterLabel: l.value,
			value: l.value,
			checked: false,
			name: c,
			possibleLabelValueId: l.id,
		}));

		filters.push({
			filterName: c,
			type: FilterType.CATEGORY,
			filterItems: filterItems,
		});
	});

	return filters;
};

const updateFilterItems = (filter, itemValue, uncheckAll) => {
	return filter.filterItems.map((filterItem) => {
		if (uncheckAll) {
			return { ...filterItem, checked: false };
		} else {
			switch (filter.type) {
				case FilterType.CHECKED:
					return { ...filterItem, checked: filterItem.value === itemValue };
				default:
					return {
						...filterItem,
						checked:
							filterItem.value === itemValue
								? !filterItem.checked
								: filterItem.checked,
					};
			}
		}
	});
};
